/**
 * Subscription Dashboard
 * Manage subscription, billing, and invoices
 */

import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  CreditCard,
  Calendar,
  Users,
  FileText,
  CheckCircle,
  AlertCircle,
  Clock,
  ArrowRight,
  Settings,
} from 'lucide-react';
import {
  getSubscription,
  createPortalSession,
  getInvoices,
  getSubscriptionAuditLogs,
  getPaymentHistory,
  Subscription,
  SubscriptionStatus,
  SubscriptionAuditEvent,
  PaymentRecord,
} from '../../services/SubscriptionService';
import TrialCountdown from './TrialCountdown';
import PaymentCheckout from './PaymentCheckout';

interface SubscriptionDashboardProps {
  organizationId: string;
}

const SubscriptionDashboard: React.FC<SubscriptionDashboardProps> = ({organizationId}) => {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [auditLogs, setAuditLogs] = useState<SubscriptionAuditEvent[]>([]);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [sub, invs, logs, pmts] = await Promise.all([
        getSubscription(organizationId),
        getInvoices(organizationId),
        getSubscriptionAuditLogs(organizationId, 50),
        getPaymentHistory(organizationId, 50),
      ]);
      setSubscription(sub);
      setInvoices(invs);
      setAuditLogs(logs);
      setPayments(pmts);
    } catch (error) {
      console.error('Failed to load subscription data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setIsOpeningPortal(true);
    try {
      const {url} = await createPortalSession(
        organizationId,
        window.location.href,
      );
      window.location.href = url;
    } catch (error) {
      console.error('Failed to open billing portal:', error);
      setIsOpeningPortal(false);
    }
  };

  const getStatusBadge = (status: SubscriptionStatus) => {
    const styles = {
      trial: 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400 border-blue-200 dark:border-blue-500/20',
      active: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20',
      expired: 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400 border-red-200 dark:border-red-500/20',
      cancelled: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400 border-slate-200 dark:border-slate-700',
      past_due: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400 border-amber-200 dark:border-amber-500/20',
    };

    const icons = {
      trial: <Clock className="w-3.5 h-3.5" />,
      active: <CheckCircle className="w-3.5 h-3.5" />,
      expired: <AlertCircle className="w-3.5 h-3.5" />,
      cancelled: <AlertCircle className="w-3.5 h-3.5" />,
      past_due: <AlertCircle className="w-3.5 h-3.5" />,
    };

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-xs font-semibold border ${styles[status]} shadow-sm`}
      >
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 space-y-6">
        <div className="relative">
          <div className="animate-spin rounded-full h-14 w-14 border-4 border-slate-200 dark:border-slate-800"></div>
          <div className="animate-spin rounded-full h-14 w-14 border-t-4 border-indigo-600 dark:border-indigo-400 absolute top-0 left-0"></div>
        </div>
        <p className="text-slate-600 dark:text-slate-400 font-medium">Loading subscription details...</p>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="relative overflow-hidden bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl rounded-3xl shadow-2xl shadow-slate-900/10 dark:shadow-black/50 p-10 text-center border border-slate-200/50 dark:border-slate-800/50">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5"></div>
          <div className="relative">
            <div className="mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg shadow-indigo-500/25 mb-6">
                <CreditCard className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-200 bg-clip-text text-transparent mb-3">
                No Subscription Found
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-400">
                Get started by setting up your subscription plan
              </p>
            </div>
            <button
              onClick={() => {
                // Don't pass invalid orgIds (default-org, default-org-id, pending, or user.id) to onboarding
                if (organizationId && 
                    organizationId !== 'default-org' && 
                    organizationId !== 'default-org-id' &&
                    organizationId !== 'pending') {
                  navigate(`/onboarding?org=${organizationId}`);
                } else {
                  navigate('/onboarding');
                }
              }}
              className="group px-8 py-4 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white rounded-2xl hover:shadow-2xl hover:shadow-purple-500/25 font-semibold text-base transition-all duration-300 hover:scale-[1.02] flex items-center space-x-3 mx-auto"
            >
              <span>Get Started</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showCheckout) {
    return (
      <PaymentCheckout
        organizationId={organizationId}
        planType={subscription.planType}
        onSuccess={() => {
          setShowCheckout(false);
          loadData();
        }}
        onCancel={() => setShowCheckout(false)}
      />
    );
  }

  return (
    <div className="space-y-8">

      {/* Trial Countdown */}
      {subscription.status === 'trial' && (
        <TrialCountdown
          subscription={subscription}
          onUpgrade={() => {
            // Don't pass invalid orgIds (default-org, default-org-id, pending, or user.id) to onboarding
            if (organizationId && 
                organizationId !== 'default-org' && 
                organizationId !== 'default-org-id' &&
                organizationId !== 'pending') {
              navigate(`/onboarding?org=${organizationId}`);
            } else {
              navigate('/onboarding');
            }
          }}
        />
      )}

      {/* Subscription Overview */}
      <div className="relative overflow-hidden bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl rounded-3xl shadow-2xl shadow-slate-900/10 dark:shadow-black/50 p-8 border border-slate-200/50 dark:border-slate-800/50">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5"></div>
        <div className="relative">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-200 bg-clip-text text-transparent mb-1">
                Subscription Overview
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-500 font-medium">
                Manage your subscription and billing
              </p>
            </div>
            {getStatusBadge(subscription.status)}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="group relative p-6 rounded-2xl bg-gradient-to-br from-slate-50 to-white dark:from-slate-800/50 dark:to-slate-800/30 border border-slate-200/50 dark:border-slate-700/50 hover:shadow-lg transition-all duration-300">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 p-3 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg shadow-indigo-500/25">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Plan Type</p>
                  <p className="text-xl font-bold text-slate-900 dark:text-white capitalize mb-1">
                    {subscription.planType}
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-400 font-medium">
                    Up to {subscription.memberLimit} users
                  </p>
                </div>
              </div>
            </div>

            {subscription.currentPeriodEnd && (() => {
              const periodEndDate = new Date(subscription.currentPeriodEnd);
              const isValidDate = !isNaN(periodEndDate.getTime());
              return isValidDate ? (
                <div className="group relative p-6 rounded-2xl bg-gradient-to-br from-slate-50 to-white dark:from-slate-800/50 dark:to-slate-800/30 border border-slate-200/50 dark:border-slate-700/50 hover:shadow-lg transition-all duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg shadow-emerald-500/25">
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
                        {subscription.status === 'trial' ? 'Trial Ends' : 'Renews On'}
                      </p>
                      <p className="text-xl font-bold text-slate-900 dark:text-white mb-1">
                        {periodEndDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                      <p className="text-sm text-slate-600 dark:text-slate-400 font-medium">
                        {periodEndDate.toLocaleDateString('en-US', { weekday: 'long' })}
                      </p>
                    </div>
                  </div>
                </div>
              ) : null;
            })()}

            <div className="group relative p-6 rounded-2xl bg-gradient-to-br from-slate-50 to-white dark:from-slate-800/50 dark:to-slate-800/30 border border-slate-200/50 dark:border-slate-700/50 hover:shadow-lg transition-all duration-300">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 p-3 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 shadow-lg shadow-pink-500/25">
                  <CreditCard className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Billing</p>
                  {subscription.stripeCustomerId ? (
                    <button
                      onClick={handleManageBilling}
                      disabled={isOpeningPortal}
                      className="mt-1 flex items-center space-x-2 text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-semibold transition-colors disabled:opacity-50 group"
                    >
                      <Settings className="w-4 h-4 group-hover:rotate-90 transition-transform duration-300" />
                      <span>{isOpeningPortal ? 'Opening...' : 'Manage Billing'}</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => setShowCheckout(true)}
                      className="text-lg font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
                    >
                      Add Payment Method
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {(subscription.status === 'expired' || subscription.status === 'past_due') && (
            <div className="mt-8 p-6 bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20 border border-red-200 dark:border-red-800/50 rounded-2xl">
              <div className="flex items-start space-x-4">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-red-800 dark:text-red-200 font-semibold mb-3">
                    {subscription.status === 'expired'
                      ? 'Your subscription has expired. Upgrade to continue using Fitzaa.'
                      : 'Payment failed. Please update your payment method.'}
                  </p>
                  <button
                    onClick={() => setShowCheckout(true)}
                    className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold transition-all duration-200 hover:shadow-lg shadow-red-500/25"
                  >
                    {subscription.status === 'expired' ? 'Upgrade Now' : 'Update Payment'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Invoices */}
      <div className="relative overflow-hidden bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl rounded-3xl shadow-2xl shadow-slate-900/10 dark:shadow-black/50 p-8 border border-slate-200/50 dark:border-slate-800/50">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5"></div>
        <div className="relative">
          <h3 className="text-2xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-200 bg-clip-text text-transparent mb-6 flex items-center">
            <div className="p-2 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg shadow-indigo-500/25 mr-3">
              <FileText className="w-5 h-5 text-white" />
            </div>
            Invoice History
          </h3>

          {invoices.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-slate-400 dark:text-slate-600 mx-auto mb-4 opacity-50" />
              <p className="text-slate-600 dark:text-slate-400 font-medium">No invoices yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {invoices.map(invoice => (
                <div
                  key={invoice.invoiceId}
                  className="group relative p-5 bg-gradient-to-br from-slate-50/50 to-white dark:from-slate-800/30 dark:to-slate-800/50 border border-slate-200/50 dark:border-slate-700/50 rounded-2xl hover:shadow-lg hover:border-indigo-200 dark:hover:border-indigo-800/50 transition-all duration-300"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-bold text-slate-900 dark:text-white text-lg mb-1">
                        {(() => {
                          const createdDate = new Date(invoice.createdAt);
                          return isNaN(createdDate.getTime()) ? 'N/A' : createdDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                        })()}
                      </p>
                      <p className="text-sm text-slate-600 dark:text-slate-400 font-medium">
                        {invoice.periodStart && invoice.periodEnd && (() => {
                          const startDate = new Date(invoice.periodStart);
                          const endDate = new Date(invoice.periodEnd);
                          if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                            return 'N/A';
                          }
                          return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
                        })()}
                      </p>
                    </div>
                    <div className="text-right ml-6">
                      <p className="font-bold text-slate-900 dark:text-white text-xl mb-2">
                        ${invoice.amount.toFixed(2)} {invoice.currency.toUpperCase()}
                      </p>
                      <span
                        className={`inline-block px-3 py-1 rounded-xl text-xs font-semibold mb-2 ${
                          invoice.status === 'paid'
                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20'
                            : 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20'
                        }`}
                      >
                        {invoice.status}
                      </span>
                      <div className="flex gap-2 justify-end">
                        {invoice.status === 'paid' && invoice.stripeReceiptUrl && (
                          <a
                            href={invoice.stripeReceiptUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-semibold hover:underline transition-colors"
                          >
                            Receipt
                          </a>
                        )}
                        {invoice.status === 'paid' && invoice.stripeHostedInvoiceUrl && (
                          <a
                            href={invoice.stripeHostedInvoiceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-semibold hover:underline transition-colors"
                          >
                            Invoice
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Subscription History */}
      <div className="relative overflow-hidden bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl rounded-3xl shadow-2xl shadow-slate-900/10 dark:shadow-black/50 p-8 border border-slate-200/50 dark:border-slate-800/50">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5"></div>
        <div className="relative">
          <h3 className="text-2xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-200 bg-clip-text text-transparent mb-6 flex items-center">
            <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg shadow-emerald-500/25 mr-3">
              <Clock className="w-5 h-5 text-white" />
            </div>
            Subscription History
          </h3>

          {auditLogs.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 text-slate-400 dark:text-slate-600 mx-auto mb-4 opacity-50" />
              <p className="text-slate-600 dark:text-slate-400 font-medium">No history available yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {auditLogs.map(log => {
              const logDate = new Date(log.timestamp);
              const isValidDate = !isNaN(logDate.getTime());

              const getEventDescription = () => {
                switch (log.eventType) {
                  case 'subscription_created':
                    return `Subscription created (${log.details.newPlanType || 'N/A'})`;
                  case 'subscription_status_changed':
                    if (log.details.previousStatus && log.details.newStatus) {
                      return `Status: ${log.details.previousStatus} → ${log.details.newStatus}${log.details.newPlanType ? ` (${log.details.newPlanType})` : ''}`;
                    }
                    return `Status changed to ${log.details.newStatus || 'N/A'}`;
                  case 'subscription_plan_changed':
                    if (log.details.previousPlanType && log.details.newPlanType) {
                      return `Plan changed: ${log.details.previousPlanType} → ${log.details.newPlanType}${log.details.newStatus ? ` (Status: ${log.details.newStatus})` : ''}`;
                    }
                    return `Plan changed to ${log.details.newPlanType || 'N/A'}`;
                  case 'trial_expired':
                    return 'Trial expired';
                  case 'payment_received':
                    const amount = log.details.metadata?.amount;
                    const currency = log.details.metadata?.currency;
                    return amount ? `Payment received: ${currency ? currency.toUpperCase() : '$'}${amount.toFixed(2)}` : 'Payment received';
                  case 'payment_failed':
                    return 'Payment failed';
                  case 'subscription_cancelled':
                    return 'Subscription cancelled';
                  case 'stripe_info_updated':
                    return 'Stripe info updated';
                  default:
                    // TypeScript guard - all cases should be handled above
                    const eventType: string = log.eventType as string;
                    return eventType.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
                }
              };

              return (
                <div
                  key={log.eventId}
                  className="group relative p-5 bg-gradient-to-br from-slate-50/50 to-white dark:from-slate-800/30 dark:to-slate-800/50 border border-slate-200/50 dark:border-slate-700/50 rounded-2xl hover:shadow-lg hover:border-indigo-200 dark:hover:border-indigo-800/50 transition-all duration-300"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-bold text-slate-900 dark:text-white text-base mb-1">
                        {getEventDescription()}
                      </p>
                      <p className="text-sm text-slate-600 dark:text-slate-400 font-medium mt-1">
                        {isValidDate ? logDate.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                      </p>
                      {log.details.reason && (
                        <p className="text-xs text-slate-500 dark:text-slate-500 mt-2 font-medium">
                          {log.details.reason}
                        </p>
                      )}
                    </div>
                    <div className="text-right ml-6">
                      <span
                        className={`inline-block px-3 py-1.5 rounded-xl text-xs font-semibold ${
                          log.eventType === 'payment_received' || log.eventType === 'subscription_created'
                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20'
                            : log.eventType === 'payment_failed' || log.eventType === 'trial_expired'
                              ? 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400 border border-red-200 dark:border-red-500/20'
                              : 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/20'
                        }`}
                      >
                        {log.details.triggeredBy || 'system'}
                      </span>
                    </div>
                  </div>
                </div>
              );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Payment History */}
      <div className="relative overflow-hidden bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl rounded-3xl shadow-2xl shadow-slate-900/10 dark:shadow-black/50 p-8 border border-slate-200/50 dark:border-slate-800/50">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5"></div>
        <div className="relative">
          <h3 className="text-2xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-200 bg-clip-text text-transparent mb-6 flex items-center">
            <div className="p-2 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 shadow-lg shadow-pink-500/25 mr-3">
              <CreditCard className="w-5 h-5 text-white" />
            </div>
            Payment History
          </h3>

          {payments.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className="w-12 h-12 text-slate-400 dark:text-slate-600 mx-auto mb-4 opacity-50" />
              <p className="text-slate-600 dark:text-slate-400 font-medium">No payment history yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {payments.map(payment => {
                const paidDate = new Date(payment.paidAt);
                const isValidDate = !isNaN(paidDate.getTime());

                return (
                  <div
                    key={payment.paymentId}
                    className="group relative p-5 bg-gradient-to-br from-slate-50/50 to-white dark:from-slate-800/30 dark:to-slate-800/50 border border-slate-200/50 dark:border-slate-700/50 rounded-2xl hover:shadow-lg hover:border-indigo-200 dark:hover:border-indigo-800/50 transition-all duration-300"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-bold text-slate-900 dark:text-white text-lg mb-1">
                          {isValidDate ? paidDate.toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                        </p>
                        <p className="text-sm text-slate-600 dark:text-slate-400 font-medium">
                          {payment.stripeReceiptNumber && `Receipt #${payment.stripeReceiptNumber}`}
                          {payment.stripeChargeId && ` • Charge: ${payment.stripeChargeId.substring(0, 12)}...`}
                        </p>
                      </div>
                      <div className="text-right ml-6">
                        <p className="font-bold text-slate-900 dark:text-white text-xl mb-2">
                          ${payment.amount.toFixed(2)} {payment.currency.toUpperCase()}
                        </p>
                        <span
                          className={`inline-block px-3 py-1 rounded-xl text-xs font-semibold mb-2 ${
                            payment.status === 'succeeded'
                              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20'
                              : payment.status === 'pending'
                                ? 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20'
                                : 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400 border border-red-200 dark:border-red-500/20'
                          }`}
                        >
                          {payment.status}
                        </span>
                        <div className="flex gap-3 justify-end">
                          {payment.stripeReceiptUrl && (
                            <a
                              href={payment.stripeReceiptUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-semibold hover:underline transition-colors"
                            >
                              Receipt
                            </a>
                          )}
                          {payment.stripeHostedInvoiceUrl && (
                            <a
                              href={payment.stripeHostedInvoiceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-semibold hover:underline transition-colors"
                            >
                              Invoice
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionDashboard;

